import { useNavigation } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { observer } from "mobx-react-lite";
import React from "react";
import { ScrollView, Tabs, useTabsContext, YGroup, YStack } from "tamagui";
import { useStores } from "../../core/stores";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Label } from "../../ui/Label";
import { BusinessAdminView } from "./BusinessAdminView";
import { BusinessAdminTemplatesList } from "./templates/BusinessAdminTemplatesList";
import { BusinessAdminUsersList } from "./templates/BusinessAdminUsersList";
import { BusinessAdminEventsList } from "./ui/BusinessAdminEventsList";
import { BusinessAdminSettings } from "./ui/BusinessAdminSettings";
import { BusinessInfoRow } from "./ui/BusinessInfoRow";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "CustomerAdmin"
>;

const Tab: React.FC<{
  label: string;
  value: string;
}> = (props) => {
  const tabsContext = useTabsContext();
  const TextComponent = tabsContext.value === props.value ? Label : Body;

  return (
    <Tabs.Tab
      paddingVertical={24}
      paddingHorizontal={32}
      borderRadius={0}
      focusStyle={{ backgroundColor: "$mutedBackground" }}
      value={props.value}
    >
      <TextComponent>{props.label}</TextComponent>
    </Tabs.Tab>
  );
};

export const BusinessAdminScreen = observer(({ route }: Props) => {
  const customerUid = route.params.customerUid;
  const { navigate } = useNavigation();
  const { businessCustomersStore } = useStores();

  const customer = businessCustomersStore.byId.get(customerUid);

  const showOverview = !route.params?.tab;

  return (
    <BusinessAdminView customerUid={customerUid}>
      <BusinessInfoRow
        title={"Create new events, templates or manage your users."}
      />

      <Tabs
        value={route.params?.tab ?? ""}
        orientation="horizontal"
        flexDirection="column"
        onValueChange={(value) => {
          navigate("CustomerAdmin", {
            customerUid: route.params.customerUid,
            tab: value,
          });
        }}
      >
        <YGroup gap={"$medium"}>
          <Tabs.List disablePassBorderRadius aria-label="Manage your account">
            <ScrollView horizontal>
              <Tab value="events" label="Events" />
              <Tab value="templates" label="Templates" />
              <Tab value="admins" label="Admins" />
              <Tab value="settings" label="Settings" />
            </ScrollView>
          </Tabs.List>
          <Tabs.Content value="events">
            {customer && <BusinessAdminEventsList customer={customer} />}
          </Tabs.Content>
          <Tabs.Content value="templates">
            {customer && <BusinessAdminTemplatesList customer={customer} />}
          </Tabs.Content>
          <Tabs.Content value="admins">
            {customer && <BusinessAdminUsersList customer={customer} />}
          </Tabs.Content>
          <Tabs.Content value="settings">
            {customer && <BusinessAdminSettings customer={customer} />}
          </Tabs.Content>
        </YGroup>
      </Tabs>

      {showOverview && (
        <YStack gap={"$medium"}>
          <Box>
            <Body>Work in Progress - show overview info</Body>
          </Box>
          <Box>
            <Body>Work in Progress - show invoice info</Body>
          </Box>
        </YStack>
      )}
    </BusinessAdminView>
  );
});
